import { computed, ref, watch, type Ref } from 'vue'
import { getWordCloudData } from '@/infrastructure/api-functions';
import { E_ANALYSIS_CHART } from '@/types';
import { downloadImage } from '@eureka/utils/download-image'
import { useGlobalState } from './use-global-store';
import { openRightAside } from '@/components/results-drawer/results-drawer';
import { useI18n } from 'vue-i18n';
import { generateImgTitle } from '@/infrastructure/utils/generate-img-title';

export function useWordCloud(chartComponent: Ref<{
    generateChartImage: () => Promise<string>,
    drawChart: () => void,
    isChartLoaded: () => boolean
    isEmptyChart: boolean
} | undefined>) {
    const { t } = useI18n();

    const title = t('wordCloud');

    const { checkedChild, companyId } = useGlobalState();

    const chartKey = E_ANALYSIS_CHART.WORD_CLOUD;

    const isFetchLoading = ref(false)

    const requestId = ref(0)

    const disableUI = computed(() => {
        return (!chartComponent.value?.isChartLoaded && !isFetchLoading.value) || chartComponent.value?.isEmptyChart
    })

    const isDownloadingImage = ref(false)

    function changeChild() {
        chartComponent.value?.drawChart()
    }

    function chartConfig() {
        return import('@/constant/chart/chartWordCloud.json').then((d) => { return d.default });
    }

    async function getWordCloudChartData() {
        isFetchLoading.value = true
        requestId.value++
        const currentRequestId = requestId.value
        if (currentRequestId === requestId.value) {
            return getWordCloudData({ child: checkedChild.value, company_id: companyId.value }).finally(
                () => {
                    isFetchLoading.value = false
                }
            )
        }
    }

    async function downloadChartAsImage() {
        if (isDownloadingImage.value) return
        isDownloadingImage.value = true
        try {
            const imageStr = await chartComponent.value?.generateChartImage()
            if (imageStr) {
                downloadImage(imageStr, generateImgTitle(title))
            }
        } finally {
            isDownloadingImage.value = false
        }
    }

    function handleDrillDown(data: Record<string, string>) {
        const { firstCode } = data
        openRightAside({
            title: t('patent'),
            data: {
                company_id: companyId.value,
                chart_type: chartKey,
                include_children: checkedChild.value,
                dimension_list: [{
                    field: 'TECH_DOMAIN_ONE_ID',
                    value: firstCode,
                }],
            },
        })
    }

    watch(
        () => checkedChild.value,
        () => {
            chartComponent.value?.drawChart()
        }
    )

    return {
        title,
        disableUI,
        isFetchLoading,
        chartKey,
        chartConfig,
        getWordCloudChartData,
        changeChild,
        downloadChartAsImage,
        handleDrillDown,
    }
}
